// Guided setup, step 6: Review. Five compact rows, each derived from the SERVER's saved data and assignments (GET .../setup summary), never from wizard memory.
// "Edit →" is a real link back to that step (it returns here afterwards and keeps this employee and this setup). A status chip says in words where each
// area stands. An area the caller cannot read is shown honestly and blocks Finish (the server refuses it with its own message). Optional blanks never block.
// Finishing does not complete tasks, approve documents or signatures, enroll benefits, change employment or access, issue equipment or call a provider.
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatLocalDate } from '../hrFormat';
import { isResolved, reviewRows, setupBlockers, STEP_LABEL, unavailableAreas, setupPath, type SetupFailure } from './setupModel';
import type { HrSetupStepStatus } from '../../../api/client';
import type { StepContext } from './setupTypes';
import { StepCard } from './stepUi';

const ICON_PATHS: Record<string, string> = {
  details: 'M12 12a4 4 0 100-8 4 4 0 000 8zm-7 8a7 7 0 0114 0',
  benefits: 'M12 21s-7-4.6-9-9a5 5 0 019-3 5 5 0 019 3c-2 4.4-9 9-9 9z',
  checklist: 'M9 6h11M9 12h11M9 18h11M4 6l1 1 2-2M4 12l1 1 2-2M4 18l1 1 2-2',
  documents: 'M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h6',
  equipment: 'M4 6h16v10H4zM2 19h20',
};
function RowIcon({ step, flagged }: { step: string; flagged: boolean }) {
  return (
    <span className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${flagged ? 'bg-amber-50 text-amber-800' : 'bg-[#EDF5FC] text-[#176A9B]'}`} aria-hidden="true">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={ICON_PATHS[step]} /></svg>
    </span>
  );
}

function StatusChip({ status, text, testId }: { status: HrSetupStepStatus; text: string; testId: string }) {
  const resolved = isResolved(status);
  const amber = status === 'needs_attention' || status === 'deferred'; // 'deferred' = legacy tolerance, shown exactly like needs_attention
  const tone = resolved ? 'bg-[#E3F1FB] text-[#0F4C75]' : amber ? 'bg-amber-50 text-amber-900 border border-amber-300' : 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${tone}`} data-testid={testId}>
      <span aria-hidden="true">{resolved ? '✓' : amber ? '!' : '–'}</span>
      {text}
    </span>
  );
}

export function ReviewStep({ ctx, finishFailure, departmentName }: {
  ctx: StepContext;
  finishFailure: SetupFailure | null;
  departmentName: (id: string | null) => string | null;
}) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { ctx.bindSaver(null); ctx.bindFooter(null); ctx.setDirty(false); void ctx.reload(); return () => ctx.bindFooter(null); }, []);
  const progress = ctx.view.progress;
  const rows = reviewRows(progress, ctx.view.summary, { date: (iso) => formatLocalDate(iso), department: departmentName });
  const blind = unavailableAreas(ctx.view.summary);
  const blockers = setupBlockers(progress, ctx.view.summary);
  const blockerLabels = blockers.map((b) => STEP_LABEL[b.step]);
  const joined = blockerLabels.length <= 1 ? blockerLabels.join('') : `${blockerLabels.slice(0, -1).join(', ')} and ${blockerLabels[blockerLabels.length - 1]}`;

  return (
    <StepCard title="Review" subtitle="Check the saved information and assignments before finishing." testId="step-review">
      <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg" data-testid="review-rows">
        {rows.map((r) => {
          const flagged = r.status === 'needs_attention' || r.status === 'deferred';
          return (
            <li key={r.step} className="flex items-center gap-3 px-4 py-3" data-testid={`review-row-${r.step}`} data-status={r.status} data-state={isResolved(r.status) ? 'complete' : r.unavailable ? 'unavailable' : 'incomplete'}>
              <RowIcon step={r.step} flagged={flagged || r.unavailable} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-sm font-semibold text-gray-900">{r.title}</span>
                  <StatusChip status={r.status} text={r.chip} testId={`review-chip-${r.step}`} />
                </div>
                <div className={`text-sm mt-0.5 ${r.unavailable ? 'text-amber-800' : 'text-gray-600'}`} data-testid={`review-line-${r.step}`}>{r.line}</div>
                {r.note && <div className="text-xs text-gray-500 mt-0.5" data-testid={`review-note-${r.step}`}>{r.note}</div>}
              </div>
              <Link to={setupPath(ctx.employeeId, r.step, 'review')} className="text-sm font-medium text-[#176A9B] hover:underline flex-shrink-0" data-testid={`review-edit-${r.step}`} aria-label={`Edit ${r.title}`}>Edit →</Link>
            </li>
          );
        })}
      </ul>

      {(blind.length > 0 || blockers.length > 0) && (
        <div role="status" className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="review-todo">
          {blockers.length > 0 && (
            <div className="font-semibold" data-testid="review-blocker-summary">{joined} {blockers.length === 1 ? 'needs' : 'need'} a decision before HR setup can finish.</div>
          )}
          <ul className="mt-1 list-disc pl-5 text-xs space-y-0.5">
            {blockers.map((b) => <li key={b.step} data-testid={`review-blocker-${b.step}`}><span className="font-medium">{STEP_LABEL[b.step]}:</span> {b.reason}</li>)}
            {blind.map((b) => <li key={b.step}>{b.label} can’t be shown with your permissions{b.capability ? ` (it ${b.reason})` : ''}, so setup can’t be finished from this account.</li>)}
          </ul>
        </div>
      )}

      {finishFailure && (
        <div role="alert" className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="finish-failure">
          <div className="font-semibold">Setup was not finished</div>
          <p className="text-xs mt-0.5" data-testid="finish-failure-message">{finishFailure.message}</p>
          {finishFailure.missing.length > 0 && (
            <ul className="mt-1 list-disc pl-5 text-xs space-y-0.5" data-testid="finish-missing">
              {finishFailure.missing.map((m, i) => <li key={`${m.step}-${i}`}><span className="font-medium">{m.step}:</span> {m.reason}{m.capability ? ` (${m.capability})` : ''}</li>)}
            </ul>
          )}
        </div>
      )}

      <div className="mt-4 rounded-md bg-[#EDF5FC] px-4 py-3 text-sm text-gray-700" data-testid="review-info-strip">
        Assigned tasks and signatures can remain pending after HR setup is finished.
      </div>
    </StepCard>
  );
}
